// src/pages/Cart.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Container from "../components/ui/Container";
import { Trash2, ShoppingBag, ArrowRight, Minus, Plus } from "lucide-react";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

// Lokale Fallback-Daten, falls die API offline ist
const FALLBACK_PRODUCTS = [
    { slug: "betonrohrfalle-standard", title: "Betonrohrfalle Standard", price_cents: 24900, image: "images/hero.webp" },
    { slug: "lockmittel-set", title: "Lockmittel Profi-Set", price_cents: 3450, image: "images/lockmittel.webp" }
];

export default function Cart({ cart, updateQty, removeFromCart, restoreCart }) {
    const [products, setProducts] = useState(FALLBACK_PRODUCTS);
    const [loading, setLoading] = useState(true);
    const [restoredNotice, setRestoredNotice] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Warenkorb aus der Erinnerungs-Mail wiederherstellen: /warenkorb?wk=<token>
    useEffect(() => {
        const token = new URLSearchParams(location.search).get("wk");
        if (!token) return;

        fetch(`${API_BASE}/api/get_cart.php?token=${encodeURIComponent(token)}`, { credentials: "omit" })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (data?.items?.length) {
                    restoreCart?.(data.items);
                    setRestoredNotice(true);
                }
            })
            .catch(() => { /* stillschweigend – der Warenkorb bleibt einfach wie er ist */ })
            .finally(() => navigate("/warenkorb", { replace: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Produkte laden (Titel/Preis/Bild)
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        fetch(`${API_BASE}/api/products.php`, { credentials: "omit" })
            .then((r) => r.json())
            .then((data) => {
                if (mounted && data && data.length > 0) setProducts(data);
            })
            .catch(() => { /* bleibt bei FALLBACK_PRODUCTS */ })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    const items = useMemo(() => {
        const map = new Map(products.map((p) => [p.slug, p]));
        return cart.map((it) => {
            const p = map.get(it.slug);
            const image = p?.image ? String(p.image).replace(/^\/+/, "") : "";
            const price = Number(it.price_cents ?? p?.effective_price_cents ?? p?.price_cents ?? 0);
            return {
                slug: it.slug,
                qty: it.qty,
                title: p?.title || it.title || it.slug,
                variant_id: it.variant_id ?? null,
                variant_label: it.variant_label ?? null,
                price_cents: price,
                image,
            };
        });
    }, [cart, products]);

    const totalCents = items.reduce((sum, it) => sum + it.price_cents * it.qty, 0);

    return (
        <section className="relative overflow-hidden min-h-screen">
            {/* Hintergrund Dekoration */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-[10%] -right-[10%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px] dark:bg-brand-500/10" />
                <div className="absolute bottom-[20%] -left-[5%] h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px] dark:bg-blue-500/5" />
            </div>

            <Container className="relative py-14 sm:py-20">
                {restoredNotice && (
                    <div className="mb-8 rounded-2xl border border-brand-200 bg-brand-50 px-5 py-4 dark:border-brand-800 dark:bg-brand-900/20">
                        <p className="text-sm font-semibold text-brand-800 dark:text-brand-200">
                            Willkommen zurück – Ihr Warenkorb wurde wiederhergestellt.
                        </p>
                        <p className="mt-1 text-xs text-brand-700/80 dark:text-brand-300/80">
                            Sie können Ihre Bestellung jetzt in Ruhe abschließen.
                        </p>
                    </div>
                )}

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200/60 dark:border-slate-800 pb-8 mb-10">
                    <div>
                        <h1 className="text-4xl font-heading font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
                            <ShoppingBag className="h-8 w-8 text-brand-600" />
                            Warenkorb
                        </h1>
                        <p className="mt-2 text-slate-500 dark:text-slate-400 text-sm sm:text-base">
                            {cart.length === 0 ? "Dein Korb wartet auf Füllung" : `Du hast ${cart.length} Artikel in deinem Warenkorb`}
                        </p>
                    </div>
                    {cart.length > 0 && (
                         <Button as={Link} to="/shop" variant="outline" className="text-sm border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800">
                            Weiter einkaufen
                         </Button>
                    )}
                </div>

                {cart.length === 0 && !loading ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <div className="h-24 w-24 rounded-full bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center mb-6">
                            <ShoppingBag className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Dein Warenkorb ist leer</h2>
                        <p className="mt-2 text-slate-500 max-w-xs mx-auto">
                            Schau dich in meinem Shop um und entdecke tolle Angebote für deine Jagd.
                        </p>
                        <Link to="/shop" className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-slate-950 dark:bg-brand-600 font-bold text-white px-8 py-4 hover:shadow-2xl transition-all active:scale-95">
                            Zum Shop gehen
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 items-start">
                        {/* Artikelliste */}
                        <div className="space-y-6">
                            {(loading ? Array.from({ length: 2 }) : items).map((it, i) =>
                                loading ? (
                                    <div key={i} className="h-32 w-full rounded-2xl border border-slate-100 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 animate-pulse" />
                                ) : (
                                    <div
                                        key={`${it.slug}:${it.variant_id ?? 'base'}`}
                                        className="group relative rounded-[2rem] border border-slate-200 dark:border-slate-800 p-6 bg-white dark:bg-slate-900 shadow-sm transition-all hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-none"
                                    >
                                        <div className="flex items-center gap-4 sm:gap-8">
                                            {/* Bild */}
                                            <div className="h-24 w-24 shrink-0 bg-slate-50 dark:bg-slate-800/50 rounded-2xl flex items-center justify-center overflow-hidden border border-slate-100 dark:border-slate-800">
                                                {it.image ? (
                                                    <img
                                                        src={`/${it.image}`}
                                                        alt={it.title}
                                                        className="max-h-20 max-w-[85%] object-contain transition duration-500 group-hover:scale-110"
                                                    />
                                                ) : <ShoppingBag className="h-8 w-8 text-slate-200" />}
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-1">Produkt</div>
                                                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white truncate">
                                                    {it.title}
                                                </h3>
                                                {it.variant_label && (
                                                    <div className="mt-1 inline-block text-[10px] uppercase font-black tracking-widest text-brand-600 bg-brand-50 dark:bg-brand-900/20 px-1.5 py-0.5 rounded">
                                                        {it.variant_label}
                                                    </div>
                                                )}
                                                <div className="mt-2 text-sm font-medium text-slate-500 tabular-nums">
                                                    {(it.price_cents / 100).toFixed(2)} € <span className="text-[10px] font-normal opacity-50">/ Stück</span>
                                                </div>
                                            </div>

                                            {/* Desktop Controls */}
                                            <div className="hidden md:flex items-center gap-8">
                                                <div className="flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-xl p-1.5 border border-slate-100 dark:border-slate-800">
                                                    <button
                                                        onClick={() => updateQty(it.slug, it.qty - 1, it.variant_id)}
                                                        className="p-1 px-3 text-slate-400 hover:text-brand-600 transition-colors"
                                                    >
                                                        <Minus className="h-4 w-4" />
                                                    </button>
                                                    <span className="w-10 text-center font-black tabular-nums text-base">
                                                        {it.qty}
                                                    </span>
                                                    <button
                                                        onClick={() => updateQty(it.slug, it.qty + 1, it.variant_id)}
                                                        className="p-1 px-3 text-slate-400 hover:text-brand-600 transition-colors"
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <div className="w-28 text-right font-heading font-black text-xl text-slate-900 dark:text-white tabular-nums">
                                                    {((it.price_cents * it.qty) / 100).toFixed(2)} €
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(it.slug, it.variant_id)}
                                                    className="p-3 bg-red-50 dark:bg-red-900/10 text-red-300 hover:text-red-600 rounded-xl transition-all"
                                                >
                                                    <Trash2 className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Mobile Footer Area inside Card */}
                                        <div className="mt-6 flex md:hidden items-center justify-between pt-6 border-t border-slate-50 dark:border-slate-800">
                                            <div className="flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-xl p-1">
                                                <button onClick={() => updateQty(it.slug, it.qty - 1, it.variant_id)} className="p-2"><Minus className="h-4 w-4"/></button>
                                                <span className="w-10 text-center font-bold text-lg">{it.qty}</span>
                                                <button onClick={() => updateQty(it.slug, it.qty + 1, it.variant_id)} className="p-2"><Plus className="h-4 w-4"/></button>
                                            </div>
                                            <div className="font-heading font-black text-xl tabular-nums">
                                                {((it.price_cents * it.qty) / 100).toFixed(2)} €
                                            </div>
                                            <button onClick={() => removeFromCart(it.slug, it.variant_id)} className="p-2.5 bg-red-50 dark:bg-red-900/10 text-red-500 rounded-lg">
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>

                        {/* Checkout Sidebar Card */}
                        <div className="sticky top-24 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-8 shadow-2xl shadow-slate-200/50 dark:shadow-none">
                            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-8">Bestellübersicht</h3>
                            
                            <div className="space-y-5 text-sm sm:text-base">
                                <div className="flex justify-between text-slate-500">
                                    <span>Zwischensumme</span>
                                    <span className="font-bold text-slate-900 dark:text-white">{(totalCents / 100).toFixed(2)} €</span>
                                </div>
                                <div className="flex justify-between text-slate-400 italic text-[11px] sm:text-xs">
                                    <span>Versandkosten</span>
                                    <span>Berechnung im Checkout</span>
                                </div>
                                <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-between items-baseline">
                                    <span className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase tracking-tight">Gesamt</span>
                                    <div className="text-right">
                                         <div className="text-3xl font-heading font-black text-brand-600 tabular-nums">{(totalCents / 100).toFixed(2)} €</div>
                                         <div className="text-[9px] text-slate-400 mt-1 uppercase font-bold tracking-wider">Inkl. Kleinunternehmer-Hinweis</div>
                                    </div>
                                </div>
                            </div>

                            <Link 
                                to="/checkout" 
                                className="mt-10 flex w-full items-center justify-center gap-3 rounded-2xl bg-slate-900 dark:bg-brand-600 px-6 py-5 text-center font-heading font-black text-lg text-white shadow-2xl shadow-slate-900/20 dark:shadow-brand-500/10 hover:scale-[1.02] active:scale-95 transition-all"
                            >
                                Zur Kasse gehen
                                <ArrowRight className="h-6 w-6" />
                            </Link>
                            
                            <p className="mt-6 text-[10px] text-center text-slate-400 italic leading-tight">
                                Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.<br/>
                                Rückgabe innerhalb von 14 Tagen möglich.
                            </p>
                        </div>
                    </div>
                )}
            </Container>
        </section>
    );
}